const PREVIEW_MAX = 140;

/** Past-tense admin headlines for Story Bible CTA sub-invocation sources. */
const COMPLETION_SOURCE_ACTION_SUMMARIES = {
  "agenticChat/generateCharacter": "Generated character",
  "agenticChat/generateCast": "Generated cast",
  "agenticChat/generateWorldbuilding": "Generated worldbuilding",
  "agenticChat/generateOutline": "Generated outline",
  "storyEngine/regenerateTrait": "Rewrote character trait",
  "storyEngine/regenerateWorldbuildingTrait": "Rewrote worldbuilding trait",
  "storyEngine/synopsis": "Generated synopsis",
  "storyEngine/rewrite/synopsis": "Rewrote synopsis",
  "storyEngine/characterCast": "Generated all characters",
  "storyEngine/worldbuildingEntry": "Generated worldbuilding",
};

/** Past-tense headlines when the completion output is a Story Bible tool call. */
const STORY_BIBLE_TOOL_ACTION_SUMMARIES = {
  generate_character: "Generated character",
  generate_cast: "Generated cast",
  generate_worldbuilding: "Generated worldbuilding",
  create_outline: "Generated outline",
  regenerate_trait: "Rewrote character trait",
  regenerate_worldbuilding_trait: "Rewrote worldbuilding trait",
  synopsis_generate: "Generated synopsis",
  synopsis_rewrite: "Rewrote synopsis",
  update_entity: "Updated Story Bible",
};

const CTA_ACTION_HEADLINE_PATTERNS = [
  { pattern: /\[CTA ACTION:\s*Generate Character\]/i, headline: "Generated character" },
  { pattern: /\[CTA ACTION:\s*Generate Element\]/i, headline: "Generated worldbuilding" },
  { pattern: /\[CTA ACTION:\s*Generate Cast\]/i, headline: "Generated cast" },
  { pattern: /\[CTA ACTION:\s*Generate Outline\]/i, headline: "Generated outline" },
  { pattern: /\[CTA ACTION:\s*Generate Synopsis\]/i, headline: "Generated synopsis" },
  { pattern: /\[CTA ACTION:\s*Rewrite Synopsis\]/i, headline: "Rewrote synopsis" },
  { pattern: /\[CTA ACTION:\s*Generate Chapter Content\]/i, headline: "Generated chapter" },
  { pattern: /\[CTA ACTION:\s*Rewrite Chapter Content\]/i, headline: "Rewrote chapter" },
  { pattern: /\[CTA ACTION:\s*(?:Generate|Rewrite) Trait\]/i, headline: "Rewrote character trait" },
];

const TOOL_DISPLAY_NAMES = {
  "draft/editPass": "Draft edit pass",
  read_file: "Read manuscript",
  manage_project_documents: "Edit manuscript",
  update_entity: "Update Story Bible",
  edit_document: "Edit document",
  generate_write_button_prose: "Write prose",
  update_series_notes: "Update series notes",
  web_search: "Web search",
  search_manuscript: "Search manuscript",
  explore: "Explore",
  generate_character: "Generate character",
  generate_worldbuilding: "Generate worldbuilding",
  "agentic-chat-prompt-intent-router": "Prompt intent router",
  "agentic-chat-manuscript-intent-router": "Manuscript intent router",
  "agentic-chat-placement-follow-up-router": "Placement follow-up router",
  "agentic-chat-starter-personalization": "Conversation starter personalization",
};

export function clipCompletionSummaryText(text, max = PREVIEW_MAX) {
  const normalized = String(text ?? "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
}

export function humanizeCompletionFunctionName(functionName) {
  if (typeof functionName !== "string" || !functionName.trim()) return "";
  const trimmed = functionName.trim();
  if (TOOL_DISPLAY_NAMES[trimmed]) return TOOL_DISPLAY_NAMES[trimmed];
  return trimmed
    .replace(/^agentic-chat-/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function humanizeToolName(toolName) {
  if (typeof toolName !== "string" || !toolName.trim()) return "";
  const trimmed = toolName.trim();
  return TOOL_DISPLAY_NAMES[trimmed] || trimmed.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function extractTextFromMessageContent(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((part) => {
      if (typeof part === "string") return part;
      if (part && typeof part === "object" && typeof part.text === "string") return part.text;
      return "";
    })
    .join(" ");
}

const AGENTIC_USER_MESSAGE_SECTION_MARKERS = [
  "\n--- Current Document ---",
  "\n--- Document comments ---",
  "\n--- Mentioned Documents ---",
  "\n--- Mentioned Characters ---",
  "\n--- Mentioned Worldbuilding ---",
  "\n--- Story Outline ---",
  "\n--- Document Targeting ---",
  "\n--- Referenced series books ---",
  "\n--- Active comment notice ---",
];

export function stripAgenticUserMessageBoilerplate(text) {
  let trimmed = String(text ?? "").trim();
  if (!trimmed) return "";

  for (const marker of AGENTIC_USER_MESSAGE_SECTION_MARKERS) {
    const idx = trimmed.indexOf(marker);
    if (idx !== -1) {
      trimmed = trimmed.slice(0, idx).trim();
    }
  }

  const firstLine = trimmed.split("\n").map((line) => line.trim()).find(Boolean);
  return firstLine ?? trimmed;
}

function isRouterClassifierUserMessage(text) {
  return /Composer mode:\s*\w+/i.test(text) && /Conversation tail:/i.test(text);
}

/** Last real user line from a prompt-intent router classifier message. */
export function extractUserPreviewFromRouterMessages(messages, maxLen = PREVIEW_MAX) {
  if (!Array.isArray(messages)) return "";
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role !== "user") continue;
    const text = extractTextFromMessageContent(message.content);
    if (!isRouterClassifierUserMessage(text)) continue;

    const tail = text.split(/Conversation tail:\s*/i)[1];
    if (!tail?.trim()) continue;

    const userLineMatches = [...tail.matchAll(/^User:\s*(.+)$/gm)];
    if (userLineMatches.length > 0) {
      const lastUserLine = userLineMatches[userLineMatches.length - 1][1].trim();
      const clipped = clipCompletionSummaryText(lastUserLine, maxLen);
      if (clipped) return clipped;
    }

    const clippedTail = clipCompletionSummaryText(tail, maxLen);
    if (clippedTail) return clippedTail;
  }
  return "";
}

export function getLastUserMessagePreview(messages, maxLen = PREVIEW_MAX) {
  if (!Array.isArray(messages)) return "";
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role !== "user") continue;
    const text = stripAgenticUserMessageBoilerplate(extractTextFromMessageContent(message.content));
    if (isRouterClassifierUserMessage(text)) continue;
    const clipped = clipCompletionSummaryText(text, maxLen);
    if (clipped) return clipped;
  }
  return "";
}

const ASSISTANT_ACTION_BY_USER_MESSAGE = [
  { pattern: /\bbrainstorm/i, headline: "Replied with brainstorming ideas in chat" },
  { pattern: /\b(feedback|comment|review)\b/i, headline: "Replied about manuscript feedback in chat" },
  { pattern: /\b(rewrite|revise|edit|fix)\b/i, headline: "Replied with editing help in chat" },
  { pattern: /\b(outline|chapter|synopsis|plot)\b/i, headline: "Replied about story structure in chat" },
  { pattern: /\b(character|worldbuilding|story bible)\b/i, headline: "Replied about Story Bible content in chat" },
  { pattern: /\?/, headline: "Answered the user's question in chat" },
];

export function extractCtaActionHeadlineFromText(text) {
  const raw = typeof text === "string" ? text : "";
  if (!raw.trim()) return null;
  for (const { pattern, headline } of CTA_ACTION_HEADLINE_PATTERNS) {
    if (pattern.test(raw)) return headline;
  }
  return null;
}

export function extractCtaActionHeadlineFromMessages(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.role !== "user") continue;
    const headline = extractCtaActionHeadlineFromText(extractTextFromMessageContent(message.content));
    if (headline) return headline;
  }
  return null;
}

export function summarizeStoryBibleToolActions(toolNames) {
  if (!Array.isArray(toolNames) || toolNames.length === 0) return null;
  const labels = [];
  const seen = new Set();
  for (const name of toolNames) {
    const label = STORY_BIBLE_TOOL_ACTION_SUMMARIES[name];
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  if (labels.length === 0) return null;
  return labels.join(", ");
}

export function formatStoryBibleCtaCompletionHeadline(completion, allToolNames, userPreview) {
  const source = typeof completion?.source === "string" ? completion.source.trim() : "";
  if (source && COMPLETION_SOURCE_ACTION_SUMMARIES[source]) {
    return COMPLETION_SOURCE_ACTION_SUMMARIES[source];
  }

  const ctaHeadline =
    extractCtaActionHeadlineFromText(userPreview) || extractCtaActionHeadlineFromMessages(completion?.messages);
  if (ctaHeadline) return ctaHeadline;

  const toolHeadline = summarizeStoryBibleToolActions(allToolNames);
  if (toolHeadline && allToolNames.length > 0 && allToolNames.every((name) => STORY_BIBLE_TOOL_ACTION_SUMMARIES[name])) {
    return toolHeadline;
  }

  return null;
}

export function formatAssistantCompletionActionSummary(userPreview) {
  const user = typeof userPreview === "string" ? userPreview.trim() : "";
  if (user) {
    for (const { pattern, headline } of ASSISTANT_ACTION_BY_USER_MESSAGE) {
      if (pattern.test(user)) return headline;
    }
    return "Replied in chat";
  }
  return "Generated an assistant reply";
}

const ROUTER_TASK_INTENT_SUMMARIES = {
  full: "general write-mode agent work",
  story_bible_edit: "a Story Bible edit",
  story_bible_direct_action: "a direct Story Bible command",
  manuscript_edit: "a manuscript edit",
  analysis: "analysis",
  support_or_bug: "support or a bug report",
};

export function formatRouterAssessmentSummary(router) {
  if (!router || router.conversation_kind === "casual") {
    return "Assessed that the chat turn looks like casual conversation";
  }

  if (router.conversation_kind === "task") {
    const intentKey = typeof router.task_intent === "string" ? router.task_intent.trim() : "";
    const intentPhrase = ROUTER_TASK_INTENT_SUMMARIES[intentKey];
    if (intentPhrase) {
      return `Assessed that the chat turn looks like it's asking for ${intentPhrase}`;
    }
    return "Assessed that the chat turn looks task-oriented";
  }

  const kind = typeof router.conversation_kind === "string" ? router.conversation_kind.trim() : "";
  if (kind) {
    return `Assessed that the chat turn looks like: ${kind.replace(/_/g, " ")}`;
  }
  return "Assessed prompt intent for this chat turn";
}

function isKnownSerializedToolId(name) {
  const trimmed = typeof name === "string" ? name.trim() : "";
  if (!trimmed) return false;
  return Boolean(TOOL_DISPLAY_NAMES[trimmed] || STORY_BIBLE_TOOL_ACTION_SUMMARIES[trimmed]);
}

export function isSerializedToolCallEnvelope(parsed) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return false;
  if (typeof parsed.name !== "string" || !parsed.name.trim()) return false;
  // Persisted OpenRouter/Gemini tool rows use { name: "<tool_id>", args: ... }.
  // Do not treat entity fields like { name: "Yoichi", ... } as tool ids.
  if (Object.prototype.hasOwnProperty.call(parsed, "args")
    || Object.prototype.hasOwnProperty.call(parsed, "arguments")) {
    return true;
  }
  // Legacy rows omitted args when undefined (JSON.stringify drops undefined values).
  return isKnownSerializedToolId(parsed.name) && Object.keys(parsed).length === 1;
}

function areSerializedToolCallArgsEmpty(args) {
  if (args == null) return true;
  if (typeof args === "string") {
    const trimmed = args.trim();
    if (trimmed === "" || trimmed === "{}" || trimmed === "[]") return true;
    try {
      return areSerializedToolCallArgsEmpty(JSON.parse(trimmed));
    } catch {
      return false;
    }
  }
  if (typeof args !== "object" || Array.isArray(args)) return false;
  const keys = Object.keys(args);
  if (keys.length === 0) return true;
  return keys.every((key) => {
    const value = args[key];
    return Array.isArray(value) && value.length === 0;
  });
}

function isSemanticallyEmptyParsedValue(parsed) {
  if (parsed == null) return true;
  if (Array.isArray(parsed)) return parsed.length === 0;
  if (typeof parsed === "object") {
    const keys = Object.keys(parsed);
    if (keys.length === 0) return true;
    return keys.every((key) => {
      const value = parsed[key];
      return Array.isArray(value) && value.length === 0;
    });
  }
  return false;
}

/** True when a persisted completion output is a tool call with no meaningful args (non-billable). */
export function isEmptySerializedToolCallOutput(value) {
  let parsed = value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "{}" || trimmed === "[]") return false;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return false;
    }
  }
  if (!isSerializedToolCallEnvelope(parsed)) return false;
  const args = parsed.args ?? parsed.arguments;
  return areSerializedToolCallArgsEmpty(args);
}

/** True when a single completion output row has no meaningful content. */
export function isSemanticallyEmptyCompletionOutput(value) {
  if (value == null) return true;
  if (isEmptySerializedToolCallOutput(value)) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "{}" || trimmed === "[]") return true;
    try {
      const parsed = JSON.parse(trimmed);
      if (isEmptySerializedToolCallOutput(parsed)) return true;
      return isSemanticallyEmptyParsedValue(parsed);
    } catch {
      return false;
    }
  }
  if (typeof value === "object") {
    if (isEmptySerializedToolCallOutput(value)) return true;
    return isSemanticallyEmptyParsedValue(value);
  }
  return false;
}

function inferToolNameFromArgs(parsed) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  if (isSerializedToolCallEnvelope(parsed)) return parsed.name.trim();
  if (parsed.conversation_kind) return "agentic-chat-prompt-intent-router";
  if (Array.isArray(parsed.canon_entries) || (typeof parsed.notes === "string" && "canon_entries" in parsed)) {
    return "update_series_notes";
  }
  if (parsed.document_id != null && (parsed.instruction != null || parsed.replace != null || parsed.insert != null)) {
    return "manage_project_documents";
  }
  if (typeof parsed.path === "string" && parsed.fields && typeof parsed.fields === "object") {
    return "update_entity";
  }
  if (typeof parsed.path === "string") return "read_file";
  if (typeof parsed.query === "string" && parsed.document_id != null) return "search_manuscript";
  if (typeof parsed.query === "string") return "web_search";
  return null;
}

function parseCompletionOutputItem(item) {
  if (item == null) return { type: "empty" };
  if (typeof item !== "string") {
    if (isSerializedToolCallEnvelope(item)) {
      return { type: "tool", name: item.name.trim() };
    }
    return { type: "empty" };
  }

  const trimmed = item.trim();
  if (!trimmed || trimmed === "{}" || trimmed === "[]") return { type: "empty" };

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object") {
      if (parsed.conversation_kind) {
        return {
          type: "router",
          conversation_kind: String(parsed.conversation_kind),
          task_intent: typeof parsed.task_intent === "string" ? parsed.task_intent : null,
        };
      }
      const toolName = inferToolNameFromArgs(parsed);
      if (toolName) return { type: "tool", name: toolName };
    }
  } catch {
    // plain text output
  }

  return { type: "text", text: trimmed };
}

export function parseCompletionOutputs(completions) {
  const toolNames = [];
  const seenTools = new Set();
  let textPreview = "";
  let router = null;

  if (!Array.isArray(completions)) {
    return { toolNames, textPreview, router };
  }

  for (const item of completions) {
    const parsed = parseCompletionOutputItem(item);
    if (parsed.type === "tool" && parsed.name && !seenTools.has(parsed.name)) {
      seenTools.add(parsed.name);
      toolNames.push(parsed.name);
    } else if (parsed.type === "router") {
      router = parsed;
    } else if (parsed.type === "text" && !textPreview) {
      textPreview = clipCompletionSummaryText(parsed.text, PREVIEW_MAX);
    }
  }

  return { toolNames, textPreview, router };
}

/**
 * @param {object} completion - Admin completion row (messages, completions, functionName, …)
 * @returns {{ headline: string, detail: string | null }}
 */
export function getCompletionAdminSummary(completion) {
  if (!completion || typeof completion !== "object") {
    return { headline: "Model call", detail: null };
  }

  const { toolNames, textPreview, router } = parseCompletionOutputs(completion.completions);
  const userPreview = router
    ? extractUserPreviewFromRouterMessages(completion.messages) || getLastUserMessagePreview(completion.messages)
    : getLastUserMessagePreview(completion.messages);
  // Only tools emitted in this completion row — not assistant tool_calls from earlier turns in messages.
  const allToolNames = toolNames;

  let headline = "";
  const storyBibleCtaHeadline = formatStoryBibleCtaCompletionHeadline(completion, allToolNames, userPreview);
  if (router) {
    headline = formatRouterAssessmentSummary(router);
  } else if (storyBibleCtaHeadline) {
    headline = storyBibleCtaHeadline;
  } else if (allToolNames.length > 0) {
    const storyBibleToolHeadline = summarizeStoryBibleToolActions(allToolNames);
    headline = storyBibleToolHeadline
      ? storyBibleToolHeadline
      : `Called tools: ${allToolNames.map(humanizeToolName).join(", ")}`;
  } else if (textPreview) {
    headline = formatAssistantCompletionActionSummary(userPreview);
  } else if (completion.functionName) {
    headline = humanizeCompletionFunctionName(completion.functionName);
  } else {
    headline = "Model call";
  }

  let detail = null;
  if (userPreview) {
    detail = `User: ${userPreview}`;
  }

  return { headline, detail };
}
